import { ModalElement } from 'inclusive-elements';

import { isTabletUp } from '../utils/breakpoints.ts';

type Drag = {
    modal: ModalElement;
    sheet: HTMLElement;
    listening: AbortController;
    startY: number;
    lastY: number;
    lastTime: number;
    velocity: number;
    // 0 until the pull starts, which is when it's measured.
    height: number;
};

let drag: Drag | undefined;

function end() {
    drag?.listening.abort();
    drag = undefined;
}

/**
 * A modal that can stop its content scrolling while a finger pulls it down.
 *
 * On iOS, a touch's moves can only be cancelled if a listener could cancel
 * them when the touch began, so the modal listens before a pull starts. It
 * only listens while the sheet is scrolled to its top, where pulls start, as
 * a listener makes every scroll wait for it.
 */
export class Modal extends ModalElement {
    connectedCallback() {
        super.connectedCallback();

        // Alerts aren't sheets, nor is anything on larger screens, and static
        // sheets can't be pulled.
        if (this.matches('.modal--alert, [static]') || isTabletUp()) return;

        this.listenForPull(true);
        this.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    }

    private onScroll = (event: Event) => {
        const el = event.target as Element;
        if (el.classList.contains('Dialog-scroll')) this.listenForPull(el.scrollTop <= 0);
    };

    // Adding or removing it again does nothing, so this can run on every scroll.
    private listenForPull(listen: boolean) {
        if (listen) this.addEventListener('touchmove', move, { passive: false });
        else this.removeEventListener('touchmove', move);
    }
}

function move(event: TouchEvent) {
    const d = drag;
    if (!d) return;

    const y = event.touches[0].clientY;

    if (!d.height) {
        if (y <= d.startY) {
            end();
            return;
        }

        // Measured before the classes change, to avoid recalculating styles.
        d.height = d.sheet.offsetHeight;

        // Stopping the scroll leaves the touched control looking pressed, so
        // turn press effects off, as a scroll does.
        document.documentElement.classList.add('is-scrolling');
        d.modal.classList.add('is-pulling');
    }

    event.preventDefault();
    d.velocity = (y - d.lastY) / Math.max(1, event.timeStamp - d.lastTime);
    d.lastY = y;
    d.lastTime = event.timeStamp;

    const pull = Math.max(0, y - d.startY);
    d.sheet.style.transform = `translateY(${pull}px)`;
    d.modal.style.setProperty('--sheet-pull', String(pull / d.height));
}

/**
 * Let sheets be swiped down to close them, as on iOS.
 *
 * Dragging down on a sheet scrolled to its top pulls it with the finger.
 * Letting go far or fast enough closes it, otherwise it springs back. Alerts
 * and static modals aren't sheets, so can't be pulled.
 */
export function enableSheetDismiss() {
    const release = () => {
        const { modal, sheet, height, startY, lastY, velocity } = drag!;
        end();
        if (!height) return;

        modal.classList.remove('is-pulling');

        const flicked = velocity > 0.5;
        const pulledFar = lastY - startY > height / 3 && velocity >= 0;
        if (flicked || pulledFar) modal.close();

        if (!modal.open) {
            // Slide out at the finger's speed, so a flick is quick and a slow
            // release speeds up. A cubic Bézier starts at y1 / x1 times its
            // average speed, so y1 comes from the finger's.
            const duration = Math.min(350, Math.max(250, (1.5 * height) / Math.max(velocity, 0.1)));
            const y1 = Math.min(1, (0.25 * velocity * duration) / height);
            const easing = `cubic-bezier(0.25, ${y1}, 0.5, 1)`;
            modal.style.setProperty('--modal-transition', `${duration}ms ${easing}`);
            return;
        }

        // The stylesheet's transitions spring it back.
        sheet.style.transform = '';
        modal.style.removeProperty('--sheet-pull');
    };

    document.addEventListener(
        'touchstart',
        (event) => {
            end();
            if (event.touches.length !== 1) return;

            // Modals that aren't sheets never pull, as they don't listen for
            // moves (see Modal).
            const target = event.target as Element;
            const sheet = target.closest<HTMLElement>('ui-modal[open] > .Dialog');
            if (!sheet) return;

            // Checked as the touch begins, not as the pull starts, because a
            // touch that begins scrolled down can scroll back to the top first.
            // Its moves can't be cancelled by then, so the content would keep
            // scrolling under a sheet that jumps by the distance scrolled.
            if (takesDrag(target, sheet)) return;

            const y = event.touches[0].clientY;
            const listening = new AbortController();
            drag = {
                modal: sheet.parentElement as ModalElement,
                sheet,
                listening,
                startY: y,
                lastY: y,
                lastTime: event.timeStamp,
                velocity: 0,
                height: 0,
            };

            const { signal } = listening;
            document.addEventListener('touchend', release, { signal });
            document.addEventListener('touchcancel', release, { signal });
        },
        { passive: true },
    );
}

/**
 * Whether something under the finger takes a downward drag itself, so the
 * sheet shouldn't be pulled.
 *
 * That's a scroller not at its top, an open menu, or anything with
 * `touch-action: none`, like glass, which stretches.
 */
function takesDrag(target: Element, sheet: HTMLElement) {
    if (target.closest('ui-popup[open]')) return true;

    for (let el: Element | null = target; el && el !== sheet.parentElement; el = el.parentElement) {
        if (el.scrollTop > 0 || getComputedStyle(el).touchAction === 'none') return true;
    }
    return false;
}
